import { create } from 'zustand';
import produce from 'immer';
import {
	MarketId,
	OrderbookGrouping,
	BidsAndAsks,
} from '@velocity-exchange/common';
import { OrderBookDisplayState } from '../../components/Orderbook/OrderbookTypes';
import useFeatureFlagStore from '../featureFlag/useFeatureFlagStore';

export interface OrderbookDisplayStore {
	set: (fn: (s: OrderbookDisplayStore) => void) => void;
	get: () => OrderbookDisplayStore;

	marketId: MarketId | null;
	orderbookDisplayState: OrderBookDisplayState;
	groupingSizeSelection: OrderbookGrouping;
	loading: boolean;
	currentUserBidsAndAsks: BidsAndAsks | null;
	isDisplayingOrderbook: boolean;
	orderbookSource: OrderbookSource;
	waitingForNewGroupingData: boolean;
	disableAutoSourceSwitching: boolean;

	setMarketId: (marketId: MarketId) => void;
	setOrderbookDisplayState: (
		state: OrderBookDisplayState,
		currentUserBidsAndAsks: BidsAndAsks
	) => void;
	setGroupingSizeSelection: (grouping: OrderbookGrouping) => void;
	setLoading: (loading: boolean) => void;
	setWaitingForNewGroupingData: (waiting: boolean) => void;
	setDisableAutoSourceSwitching: (disable: boolean) => void;
	reset: () => void;
}

export enum OrderbookSource {
	WEBSOCKET = 'WEBSOCKET',
	POLLING = 'POLLING',
	BLOCKCHAIN = 'BLOCKCHAIN',
}

export const getBestAvailableOrderbookSource = (): OrderbookSource => {
	const websocketDisabled = useFeatureFlagStore
		.getState()
		.flagEnabled('DISABLE_WEBSOCKET_DLOB_SOURCE');

	return websocketDisabled
		? OrderbookSource.POLLING
		: OrderbookSource.WEBSOCKET;
};

const initialStateProps: Pick<
	OrderbookDisplayStore,
	| 'marketId'
	| 'orderbookDisplayState'
	| 'groupingSizeSelection'
	| 'loading'
	| 'currentUserBidsAndAsks'
	| 'isDisplayingOrderbook'
	| 'orderbookSource'
	| 'waitingForNewGroupingData'
	| 'disableAutoSourceSwitching'
> = {
	marketId: null,
	orderbookDisplayState: {
		bids: [],
		asks: [],
		bidTotalSize: 0,
		askTotalSize: 0,
		containerCallbackRef: () => {},
	},
	groupingSizeSelection: 100 as OrderbookGrouping,
	loading: true,
	currentUserBidsAndAsks: null,
	isDisplayingOrderbook: false,
	orderbookSource: getBestAvailableOrderbookSource(),
	waitingForNewGroupingData: false,
	disableAutoSourceSwitching: false,
};

const useOrderbookDisplayStore = create<OrderbookDisplayStore>(
	(set, get): OrderbookDisplayStore => {
		const setState = (fn: (s: OrderbookDisplayStore) => void) =>
			set(produce(fn));

		return {
			set: setState,
			get: () => get(),

			...initialStateProps,

			setMarketId: (marketId: MarketId) => {
				setState((s) => {
					s.marketId = marketId;
				});
			},

			setOrderbookDisplayState: (
				state: OrderBookDisplayState,
				currentUserBidsAndAsks: BidsAndAsks
			) => {
				setState((s) => {
					s.orderbookDisplayState = state;
					s.currentUserBidsAndAsks = currentUserBidsAndAsks;
				});
			},

			setGroupingSizeSelection: (grouping: OrderbookGrouping) => {
				setState((s) => {
					s.groupingSizeSelection = grouping;
					s.waitingForNewGroupingData = true;
				});
			},

			setLoading: (loading: boolean) => {
				setState((s) => {
					s.loading = loading;
				});
			},

			setWaitingForNewGroupingData: (waiting: boolean) => {
				setState((s) => {
					s.waitingForNewGroupingData = waiting;
				});
			},

			setDisableAutoSourceSwitching: (disable: boolean) => {
				setState((s) => {
					s.disableAutoSourceSwitching = disable;
				});
			},

			reset: () => {
				setState((s) => {
					Object.assign(s, initialStateProps);
				});
			},
		};
	}
);

export default useOrderbookDisplayStore;
